import { useMemo, useState } from "react";
import { FlaskConical, Plus, Search } from "lucide-react";
import { trpc } from "../../trpc";
import SectionTitle from "../../components/SectionTitle";
import ExperimentDialog, { Experiment } from "../../components/experiments/ExperimentDialog";
import { formatPercentage } from "../../lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Alert } from "@/components/ui/alert";

type ActiveFilter = "all" | "active" | "inactive";

const ExperimentsContainer = () => {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("active");
  const [selected, setSelected] = useState<Experiment | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const experimentsQuery = trpc.experiments.list.useQuery({ order: "desc" });
  const { data, isLoading, isError, error, refetch } = experimentsQuery;

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    const term = search.trim().toLowerCase();
    return items.filter((experiment) => {
      if (activeFilter === "active" && !experiment.isActive) return false;
      if (activeFilter === "inactive" && experiment.isActive) return false;
      if (!term) return true;
      return (
        experiment.id.toLowerCase().includes(term) ||
        (experiment.description ?? "").toLowerCase().includes(term)
      );
    });
  }, [data?.items, search, activeFilter]);

  const handleCreate = () => {
    setSelected(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (experiment: Experiment) => {
    setSelected(experiment);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelected(null);
  };

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Experiments"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Experiments" }]}
        description="Rollouts por porcentaje con overrides por merchant y por cliente"
        actions={
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-1" />
            Nuevo experimento
          </Button>
        }
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
            <div className="space-y-2 flex-1 min-w-[160px]">
              <Label htmlFor="active-filter">Estado</Label>
              <Select value={activeFilter} onValueChange={(value) => setActiveFilter(value as ActiveFilter)}>
                <SelectTrigger id="active-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activos</SelectItem>
                  <SelectItem value="inactive">Inactivos</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex-[2] min-w-[300px]">
              <Label htmlFor="experiment-search">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="experiment-search"
                  placeholder="Buscar por ID o descripción..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Spinner className="w-8 h-8" />
        </div>
      )}

      {isError && (
        <Alert status="error">
          <p className="font-semibold">Error loading experiments</p>
          <p className="text-sm">{error?.message || "Unknown error occurred"}</p>
        </Alert>
      )}

      {data && filtered.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <FlaskConical className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-lg font-medium text-gray-900">
              {data.items.length === 0 ? "No hay experimentos" : "Ningún experimento coincide"}
            </p>
          </CardContent>
        </Card>
      )}

      {filtered.length > 0 && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Descripción</th>
                  <th className="px-4 py-3">Activo</th>
                  <th className="px-4 py-3">Porcentaje</th>
                  <th className="px-4 py-3">Merchants</th>
                  <th className="px-4 py-3">Clientes</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((experiment) => (
                  <tr key={experiment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{experiment.id}</td>
                    <td className="px-4 py-3 max-w-72 truncate">{experiment.description || "-"}</td>
                    <td className="px-4 py-3">
                      <Badge variant={experiment.isActive ? "secondary" : "outline"}>
                        {experiment.isActive ? "Sí" : "No"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">{formatPercentage(experiment.percentage, 0)}</td>
                    <td className="px-4 py-3">{experiment.merchantOverrides.length}</td>
                    <td className="px-4 py-3">{experiment.customerOverrides.length}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(experiment)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {isDialogOpen && (
        <ExperimentDialog
          key={selected?.id ?? "new"}
          experiment={selected}
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
};

export default ExperimentsContainer;
